import { inject, Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { TaasUserDetails, TaasJwtTokenDetails } from 'utimaco-common-ui-angular';

@Injectable({
  providedIn: 'root'
})
export class TaasUserService {

  public ROLE_NAME_PORTAL_VIEWER: string = "TaasPortalApi:viewer";
  public ROLE_NAME_GLOBAL_VIEWER: string = "global-viewer";
  public ROLE_NAME_PORTAL_ORG_ADMIN: string = "TaasPortalApi:org-admin";
  public ROLE_NAME_GLOBAL_ORG_ADMIN: string = "global-org-admin";
  public ROLE_NAME_GUEST_USER: string = "guest-user";

  private keycloakService: KeycloakService = inject(KeycloakService);

  performLogout() {
    this.keycloakService?.logout();
  }

  /**
   * Extract the current users details from the JWT token
   * @returns 
   */
  async getCurrentUserDetails(): Promise<TaasUserDetails> {
    const tokenString = await this.keycloakService.getToken();
    const base64Url = tokenString.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const tokenObject: TaasJwtTokenDetails = JSON.parse(jsonPayload);

    const realmRoles = tokenObject.realm_access?.roles || [];
    const resourceRoles = Object.entries(tokenObject.resource_access || {})
      .flatMap(([resourceName, resource]: [string, { roles: string[] }]) =>
        (resource.roles || []).map((role: string) => `${resourceName}:${role}`)
      );
    const userDetails: TaasUserDetails = {
      givenName: tokenObject.given_name,
      familyName: tokenObject.family_name,
      email: tokenObject.email,
      initials: tokenObject.given_name.trim().substring(0, 1) + tokenObject.family_name.trim().substring(0, 1),
      roles: [...new Set([...realmRoles, ...resourceRoles])]
    }
    return userDetails;
  }

  /**
   * Checks if the JWT token contains explicitly the given role name (so if the current user has a specific role)
   * @param roleName the role name to search for in the user jwt token
   * @returns 
   */
  async hasRole(roleName: string): Promise<boolean> {
    return this.getCurrentUserDetails().then(taasUserDetails => {
      return taasUserDetails.roles?.includes(roleName) ? true : false;
    });
  }

  /**
   * Checks if the JWT token contains one ore more of the given role names
   * @param roleNames the role names to search for in the user jwt token
   * @returns 
   */
  async hasAnyRole(roleNames: string[]): Promise<boolean> {
    return this.getCurrentUserDetails().then(taasUserDetails => {
      return taasUserDetails.roles?.some(existingRoleName => roleNames.includes(existingRoleName)) ? true : false;
    });
  }

  /**
   * Checks if the current user has the guest-user role.
   * This is not an Exclusive check, but only if the user has this role (he could still have other roles).
   * The TaaS basic concept is to remove the guest role when other roles are added, so this check is in most cases sufficient
   * @returns 
   */
  async isGuestUser(): Promise<boolean> {
    return this.hasRole(this.ROLE_NAME_GUEST_USER);
  }

  /**
   * checks if the use has thee needed role to perform org admin specific tasks in the portal.
   * Checks if the user if either global org admin or portal org admin
   * @returns 
   */
  async isOrgAdmin(): Promise<boolean> {
    return this.hasAnyRole([this.ROLE_NAME_GLOBAL_ORG_ADMIN, this.ROLE_NAME_PORTAL_ORG_ADMIN]);
  }
}
